import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { HeroSlide } from "@/mocks/home";

interface HeroCarouselProps {
  slides: HeroSlide[];
}

export default function HeroCarousel({ slides }: HeroCarouselProps) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % slides.length);
    }, 7000);
    return () => window.clearInterval(timer);
  }, [paused, slides.length]);

  const goPrev = () => setActive((prev) => (prev - 1 + slides.length) % slides.length);
  const goNext = () => setActive((prev) => (prev + 1) % slides.length);
  const current = slides[active];

  return (
    <section
      className="relative h-[600px] w-full overflow-hidden bg-primary-950 md:h-[700px]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-label="精选焦点内容"
    >
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === active ? "z-10 opacity-100" : "z-0 opacity-0"
          }`}
          aria-hidden={index !== active}
        >
          <img
            src={slide.image}
            alt={`${slide.title} 焦点图`}
            title={`${slide.title} 翔田千里影视作品`}
            className={`h-full w-full object-cover object-center ${index === active ? "animate-hero-zoom" : ""}`}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/70 to-primary-950/25"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-transparent to-primary-950/50"></div>
          <div className="absolute inset-0 film-grain opacity-30"></div>
        </div>
      ))}

      <div className="container-page relative z-20 flex h-full flex-col justify-end pb-20 pt-32 md:justify-center md:pb-16 md:pt-28">
        <div key={current.id} className="max-w-3xl animate-fade-in">
          <div className="flex items-center gap-3">
            <span className="h-px w-8 bg-accent-400"></span>
            <span className="text-[11px] uppercase tracking-wider2 text-accent-300">{current.kicker}</span>
          </div>
          <h1 className="mt-4 font-heading text-4xl leading-tight text-background-50 md:text-6xl">
            {current.title}
          </h1>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-background-50/75 md:mt-5 md:text-base">
            {current.subtitle}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-2">
            {current.meta.map((meta) => (
              <span
                key={meta}
                className="rounded-md border border-background-50/20 bg-background-50/10 px-3 py-1.5 text-[11px] text-background-50/80 backdrop-blur"
              >
                {meta}
              </span>
            ))}
          </div>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <Link
              to={current.to}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-medium text-primary-950 transition-colors hover:bg-accent-400"
            >
              {current.cta}
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
            <Link
              to="/works"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 px-6 py-3 text-sm text-background-50 transition-colors hover:border-background-50/60 hover:bg-background-50/10"
            >
              <i className="ri-film-line" aria-hidden="true"></i>
              全部影视作品
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-6 z-20">
        <div className="container-page flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            {slides.map((slide, index) => (
              <button
                key={slide.id}
                type="button"
                onClick={() => setActive(index)}
                aria-label={`切换到第 ${index + 1} 张焦点图`}
                className={`h-1 cursor-pointer rounded-full transition-all ${
                  index === active ? "w-10 bg-accent-400" : "w-5 bg-background-50/30 hover:bg-background-50/60"
                }`}
              ></button>
            ))}
            <span className="ml-3 text-[11px] tracking-wide text-background-50/60">
              {String(active + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={goPrev}
              aria-label="上一张"
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-50/30 text-background-50/80 transition-colors hover:border-accent-400 hover:text-accent-300 md:h-10 md:w-10"
            >
              <i className="ri-arrow-left-s-line text-lg" aria-hidden="true"></i>
            </button>
            <button
              type="button"
              onClick={goNext}
              aria-label="下一张"
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-50/30 text-background-50/80 transition-colors hover:border-accent-400 hover:text-accent-300 md:h-10 md:w-10"
            >
              <i className="ri-arrow-right-s-line text-lg" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}